import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  useNavigate,
} from 'react-router';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import { toast } from 'react-toastify';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Spinner,
  NestedWorkout,
  PersonalWorkout,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

type Props = {
  id: number | null,
  workout: any,
  basePath: string,
  parentType: 'personalWorkouts',
  resourceType: 'warmups' | 'cooldowns',
};

const AdditionalPersonalWorkoutPage = ({
  id,
  workout,
  basePath,
  parentType,
  resourceType,
}: Props) => {
  const navigate = useNavigate();
  const mapState = useCallback(state => ({
    status: getStatus(
      state,
      [
        `${resourceType}.requests.getFullAdditionalWorkout.status`,
        `${resourceType}.meta[${id}].readStatus`,
      ],
      true,
    ),
    options: coreSelectors.getEntitySearchOptions(
      resourceType,
      'library',
    )(state),
    folderRoots: coreSelectors.getFolderRoots(state),
    additionalWorkout: id ? coreSelectors.getResourceById(
      resourceType,
      id,
    )(state) : null,
    isPro: coreSelectors.getProfile('is_pro')(state),
  }), [resourceType, id]);

  const {
    isPro,
    status,
    options,
    folderRoots,
    additionalWorkout,
  } = useSelector(mapState);

  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      dispatch(
        coreActions.requestFullWorkout({
          id,
          resourceType: parentType,
          addtionalType: resourceType,
        }),
      );
    }
  }, [id]);

  const type = resourceType === 'warmups' ? 'warmup' : 'cooldown';
  const fillPath = `${basePath}/${workout.id}/${type}`;

  const analyticTrack = (attrs: any) => {
    dispatch({
      type: 'GC_ANALYTICS',
      eventKey: type === 'warmup' ? 'added_warmup' : 'added_cooldown',
      attrs: {
        id: attrs.id,
        name: attrs.name,
        warmupId: attrs.warmup_id,
        cooldownId: attrs.cooldown_id,
      },
    });
  };

  const handleAdd = (payload: any, callbacks = {}) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: workout.id,
        payload,
        resourceType: parentType,
        callbacks,
      }),
    );
  };

  const handleSave = () => {
    dispatch(
      coreActions.requestSaveAsTemplate({
        id: id as number,
        payload: {
          folder_id: type === 'warmup' ? folderRoots.warmupId : folderRoots.cooldownId,
        },
        resourceType,
        callbacks: {
          onSuccess: () => {
            toast.success(`${i18n.t('Workout')} was saved to your library`);
          },
        },
      }),
    );
  };

  const handleSubmit = (payload, actions) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: id as number,
        payload,
        endNotify: true,
        requestType: parentType,
        resourceType,
        callbacks: {
          ...actions,
          onSuccess: () => {
            navigate(fillPath);
          },
        },
      }),
    );
  };

  const handleDelete = () => {
    const payload = type === 'warmup'
      ? { warmup_id: null }
      : { cooldown_id: null };
    dispatch(
      coreActions.requestUpdateEntity({
        id: workout.id,
        payload,
        resourceType: parentType,
        callbacks: {
          onSuccess: () => {
            navigate(fillPath);
          },
        },
      }),
    );
  };

  return id ? (
    <Flex fillSpace>
      {status.succeeded && (
        <Routes>
          <Route
            index
            element={(
              <PersonalWorkout
                type={type}
                isPro={isPro}
                readOnly
                workout={additionalWorkout}
                workoutType="PersonalWorkout"
                resourceType={resourceType!}
                onEdit={() => {
                  navigate(`${fillPath}/edit`);
                }}
                onSubmit={() => { }}
                onSaveToLibrary={isPro ? handleSave : undefined}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <PersonalWorkout
                type={type}
                isPro
                workout={additionalWorkout}
                workoutType="PersonalWorkout"
                resourceType={resourceType!}
                onEdit={() => {
                  navigate(`${fillPath}/edit`);
                }}
                onCancel={() => {
                  navigate(fillPath);
                }}
                onSubmit={handleSubmit}
                onRemove={handleDelete}
                onSaveToLibrary={handleSave}
              />
            )}
          />
        </Routes>
      )}
      {status.pending && (
        <Spinner message="Loading..." />
      )}
    </Flex>
  ) : (
    <NestedWorkout
      type={type}
      options={options}
      readOnly={!isPro}
      onRequestAdd={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: value }
          : { cooldown_id: value };

        handleAdd(payload, { onSuccess: analyticTrack });
      }}
      onRequestAddNew={({ value }) => {
        const payload = type === 'warmup'
          ? { warmup_id: '0' }
          : { cooldown_id: '0' };

        const callbacks = {
          onSuccess: (result: any) => {
            navigate(`${fillPath}/edit`);
            dispatch(
              coreActions.requestUpdateEntity({
                id: type === 'warmup' ? result.warmup_id : result.cooldown_id,
                payload: { name: value },
                requestType: parentType,
                resourceType,
              }),
            );
            analyticTrack(result);
          },
        };

        handleAdd(payload, callbacks);
      }}
    />
  );
};

export default AdditionalPersonalWorkoutPage;
